import { useEffect, useRef, useState } from "react";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { fetchJudgeLanguages, toSandboxLanguages } from "../../utils/judgeLanguages";

/** What the sandbox shows when nobody hands it a challenge: print Hello, World! */
export const DEFAULT_CHALLENGE = {
  question: "Print Hello, World!",
  sampleCode: {
    java: `public class Main {
  public static void main(String[] args) {
    System.out.println("Hello, World!");
  }
}`,
    javascript: `console.log("Hello, World!");`
  },
  testCases: [{ input: "", expected: "Hello, World!" }],
  description: "Write a program to print 'Hello, World!' in the specified language.",
};

/** The editor's settings, the same on both screens. */
export const SANDBOX_EDITOR_OPTIONS = { fontSize: 15, formatOnType: true, minimap: { enabled: false }, wordWrap: true, contextmenu: false };

/**
 * A coding challenge in the sandbox: the languages the code runner has, the code, a run
 * on custom input, a run of every test, and handing the results to `onSubmit`. Shared
 * by CodingChallenge and the laptop sandbox, so both send the runner the same thing.
 * `sampleCode` is keyed by Monaco language id.
 */
const useCodingChallenge = ({
  sampleCode, testCases, challengeId, onSubmit,
}) => {
  const [lang, setLang] = useState("");
  const [code, setCode] = useState("");
  const [languages, setLanguages] = useState([]);
  const [languagesReady, setLanguagesReady] = useState(false);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const editorRef = useRef(null);
  const [activity, setActivity] = useState("");
  const loggedInUser = getCurrentUser();
  const isDevMode = loggedInUser?.mode === "dev" || false;

  // Load languages from Judge0 API (cached in sessionStorage)
  useEffect(() => {
    fetchJudgeLanguages().then(raw => {
      const langs = toSandboxLanguages(raw);
      setLanguages(langs);
      setLanguagesReady(true);
      if (langs.length > 0 && !lang) {
        const first = langs.find(l => sampleCode?.[l.value]) || langs[0];
        setLang(first.value);
        setCode(sampleCode?.[first.value] || "");
      }
    });
  // eslint-disable-next-line
  }, []);

  const onMount = (editor, monaco) => {
    editorRef.current = editor;
    editor.focus();

    editor.onKeyDown((event) => {
      if (isDevMode) return; // Skip tracking in dev mode
      const { keyCode, ctrlKey, metaKey } = event;
      if ((keyCode === 33 || keyCode === 52) && (metaKey || ctrlKey)) {
        event.preventDefault();
        setActivity("copypaste");
      }
    });
  };

  const handleLanguageChange = (e) => {
    const value = e.target.value;
    setLang(value);
    setCode(sampleCode?.[value] || "");
    setResults([]);
    setOutput("");
    setInput("");
    setError("");
  };

  const handleRun = async () => {
    setRunning(true);
    setResults([]);
    setOutput("");
    setError("");
    const langId = (languages.find(l => l.value === lang) || {}).id;
    if (!langId) return;
    try {
      const { data } = await apiClient.post("/code/run", {
        source_code: code,
        language_id: langId,
        stdin: input,
      });
      const runOutput = (data.stdout || data.stderr || data.compile_output || "").trim();
      setOutput(runOutput);
    } catch (err) {
      setError("Could not run code. Please try again.");
    }
    setRunning(false);
  };

  const handleSubmit = async (e) => {
    setSubmitted(true);
    console.log("submitting manually", e, results);
    let fresults = null;
    if (Array.isArray(e)) {
      fresults = e;
    } else if (Array.isArray(results)) {
      fresults = results;
    }
    console.log("Submitting code...", fresults);
    if (!Array.isArray(fresults) || fresults.length === 0) {
      setError("No test cases run. Please run the code first.");
      return;
    }
    console.log("Submitting results:", fresults);
    const passedCount = Array.isArray(fresults) ? fresults.filter(r => r.passed).length : 0;
    const total = Array.isArray(fresults) ? fresults.length : 0;
    const allPassed = passedCount === total;

    if (onSubmit) {
      await onSubmit({
        questionId: challengeId,
        code,
        language: lang,
        results: fresults,
        passedCount,
        total,
        status: allPassed ? "passed" : "failed",
        notes: allPassed
          ? "All test cases passed"
          : `Failed ${total - passedCount} out of ${total} test cases`
      });
    }
  };

  const handleRunAllCases = async (action) => {
    setRunning(true);
    setResults([]);
    setError("");
    const langId = (languages.find(l => l.value === lang) || {}).id;
    if (!langId) return;
    try {
      const { data } = await apiClient.post("/code/run-batch", {
        source_code: code,
        language_id: langId,
        testCases: testCases.map(tc => ({ input: tc.input, expected: tc.expected })),
      });
      setResults(data.results);
      if (action === "submit") {
        await handleSubmit(data.results);
      }
    } catch (err) {
      setError("Could not run test cases. Please try again.");
    }
    setRunning(false);
  };

  return {
    lang,
    code,
    setCode,
    languages,
    languagesReady,
    input,
    setInput,
    output,
    results,
    running,
    error,
    submitted,
    activity,
    onMount,
    handleLanguageChange,
    handleRun,
    handleRunAllCases,
    handleSubmit,
  };
};

export default useCodingChallenge;
